import { Anchor, Badge, Grid, Group, Progress, Stack, Text, useMantineTheme } from '@mantine/core';
import { CoverageText, PercentCovered, Header, NoEffectivePolicies, Container } from '../../TagPolicies';
import { PieChart, PieChartSettings } from '@root/Components/Charts/PieChart';
import { DataGrid } from '@root/Components/DataGrid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomColors } from '@root/Design/Themes';
import { useDi } from '@root/Services/DI';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import { ColumnConfig, DataGridState } from '@root/Components/DataGrid/Models';
import { useNav } from '@root/Services/NavigationService';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';
import { AwsEffectiveTagPolicy, BaseResource, IQueryExpr, Query } from '@apis/Resources/model';
import { getAccountGetAccountConnectionStatus, getAccountGetCompanyAccounts } from '@apis/Customers';
import { postResourcesMultiQuery, postResourcesQuery, postTagPolicyGetPolicies, QueryExpr, QueryResult } from '@apis/Resources';
import { useCompany } from '@root/Components/Router/CompanyContent';
import { PanelBody, PanelHeader } from '@root/Design/Layout';
import { exprBuilder, queryBuilder } from '@root/Services/QueryExpr';
import { AsyncBundler } from '@root/Services/AsyncBundler';
import { Account, AccountConnectedStatus } from '@apis/Customers/model';
import { FillerSwitch } from '@root/Design/Filler';
import { TagKeyDataFilter } from '@root/Components/Resources/Common/ResourceTags';

export const AwsTagPolicySection = ({ tag }: { tag?: string }) => {
    const { colors } = useMantineTheme();
    const [numInCompliance, setNumInCompliance] = useState(0);
    const [totalResources, setTotalResources] = useState(0);
    const [totalTagKeys, setTotalTagKeys] = useState(0);
    const [policyTagKeys, setPolicyTagKeys] = useState(0);
    const tagKeys = useMemo(() => ({ keys: [] as string[] }), []);
    const [numNotInCompliance, setNumNotInCompliance] = useState(0);
    const [effPolicyDataItems, setEffPolicyDataItems] = useState<EffectiveTagPoliciesDataItem[]>();
    const [selectedEffectiveTagPolicy, setSelectedEffectiveTagPolicy] = useState<EffectiveTagPoliciesDataItem>();
    const [effPolicies, setEffPolicies] = useState<AwsEffectiveTagPolicy[]>();
    const fmtSvc = useDi(FormatService);
    const asyncBundler = useDi(AsyncBundler);
    const company = useCompany()!;
    async function bundledResourceRequest<T>(query: Query) {
        return asyncBundler.bundle('policy-compliance-counts', query, (r) => postResourcesMultiQuery(r, { companyId: company.Id })) as QueryResult<T>;
    }

    type EffectiveTagPoliciesDataItem = {
        id: number;
        accountId: string;
        name: string;
        statements: number;
        compliant: number;
        nonCompliant: number;
        tags: string[];
        connected: boolean;
    };

    useEffect(() => {
        (async () => {
            const accounts = await getAccountGetCompanyAccounts();
            const accountsConnectedStatus = await getAccountGetAccountConnectionStatus({ companyId: company.Id });
            const effectivePolicies = await postTagPolicyGetPolicies();
            setEffPolicies(effectivePolicies);
            const policyTagKeys = new Set<string>();

            const accountLookup = accounts.reduce((result, item) => result.set(item.Id ?? 0, item), new Map<number, Account>());
            const connectedLookup = accountsConnectedStatus.reduce(
                (result, item) => result.set(item.AccountId ?? 0, item),
                new Map<number, AccountConnectedStatus>()
            );
            if (effectivePolicies) {
                const getComplianceCounts = async (policy: AwsEffectiveTagPolicy) => {
                    const rules = policy.Statements?.map((stmt) => stmt.ComplianceRule).filter((r) => !!r) ?? [];
                    const result = { compliant: 0, nonCompliant: 0 };
                    if (rules.length) {
                        const query = queryBuilder<{ AccountID: number }>()
                            .where((b) => b.model.AccountID.eq(policy.AccountId!))
                            .select((b) => ({
                                compliant: rules.length ? b.countIf(b.and(...rules.map((r) => b.fromExpr(r as QueryExpr)))) : b.count(),
                            }))
                            .build();
                        const queryResult = await bundledResourceRequest<{ compliant: number }>(query);
                        result.compliant = queryResult.Results?.[0]?.compliant ?? 0;
                        result.nonCompliant = (queryResult.Count ?? 0) - result.compliant;
                    }
                    return result;
                };
                const totalResourceRequest = bundledResourceRequest<{}>({ Take: 0 }).then((r) => setTotalResources(r.Count ?? 0));
                const uniquePolicyTagKeys = bundledResourceRequest<{ tagKeys: number }>(
                    queryBuilder<{ ['Tags.Key']: string }>()
                        .select((b) => ({ tagKeys: b.countUniqueValues(b.model['Tags.Key']) }))
                        .build()
                ).then((r) => setTotalTagKeys(r.Results?.[0].tagKeys ?? 0));
                const policyPromises = effectivePolicies.map(async (policy) => {
                    const account = accountLookup.get(policy.AccountId ?? 0);
                    const connected = connectedLookup.get(policy.AccountId ?? 0);
                    const tags: string[] = [];
                    policy.Statements?.forEach((stmt) => {
                        if (stmt.Tag) {
                            policyTagKeys.add(stmt.Tag ?? '');
                            tags.push(stmt.Tag);
                        }
                    });
                    const { compliant, nonCompliant } = await getComplianceCounts(policy);

                    return {
                        id: policy.AccountId!,
                        accountId: account?.AwsAccountId ?? '',
                        name: account?.Name ?? '',
                        statements: policy.Statements?.length ?? 0,
                        tags,
                        compliant,
                        nonCompliant,
                        total: compliant + nonCompliant,
                        connected: !!connected?.ConnectionStatus,
                    };
                });

                const policies = await Promise.all(policyPromises);
                setEffPolicyDataItems(policies);
                await Promise.all([totalResourceRequest, uniquePolicyTagKeys]);

                var numCompliant = policies.reduce((a, v) => (a = a + v.compliant), 0);
                var numNoncompliant = policies.reduce((a, v) => (a = a + v.nonCompliant), 0);
                setPolicyTagKeys(policyTagKeys.size);
                const sortedKeys = [...policyTagKeys].sort((a, b) => (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' }));
                tagKeys.keys.splice(0, Infinity, ...sortedKeys);
                setNumInCompliance(numCompliant);
                setNumNotInCompliance(numNoncompliant);
            }
        })();
    }, []);

    function PolicyStringLink({
        connected,
        name,
        value,
        policy,
    }: {
        connected: boolean;
        name: string;
        value: string;
        policy: EffectiveTagPoliciesDataItem;
    }) {
        const fmtSvc = useFmtSvc();
        const acctName = fmtSvc.awsAccountId(value || '000000000000');
        const label = `${acctName} - ${name || 'Name Unknown'}`;
        return connected ? (
            <Anchor color={connected ? undefined : 'gray'} onClick={() => (connected ? loadEffectiveTagPolicies(policy) : null)}>
                {label}
            </Anchor>
        ) : (
            <>
                {!connected ? <Badge color="error">Not Connected</Badge> : ''}
                {label}
            </>
        );
    }

    const routeLoader = useDi(BasicRouteLoader);
    const { getDescendUrl, goto } = useNav(routeLoader.getTopRouteMeta());
    const loadEffectiveTagPolicies = useCallback((policy?: EffectiveTagPoliciesDataItem) => {
        if (policy != null && policy.connected) {
            const allActivityUrl = getDescendUrl('tag-policy-details', {
                id: policy.id.toString(),
                accountId: policy.accountId,
                cloudProvider: 'aws',
            });
            goto(allActivityUrl);
        }
    }, []);
    const accountValueList = useMemo(() => effPolicyDataItems?.map((p) => `${p.accountId} - ${p.name}`) ?? [], [effPolicyDataItems]);
    const cols = useMemo(() => {
        return [
            {
                header: 'Account ID',
                defaultWidth: 300,
                id: 'accountId',
                sortField: 'accountId',
                type: 'string',
                align: 'left',
                accessor: (item) => `${item.accountId} - ${item.name}`,
                cellRenderer: (item) => {
                    return <PolicyStringLink connected={item.connected} name={item.name} value={item.accountId} policy={item}></PolicyStringLink>;
                },
                filter: {
                    filterField: 'accountId',
                    filterType: 'string',
                    name: 'Account ID',
                    options: {
                        getValueProvider: () => accountValueList?.map((p) => ({ label: p, value: p })) ?? [],
                    },
                },
            },
            {
                header: 'Effective Statements',
                accessor: 'statements',
                sortField: 'statements',
                defaultWidth: 200,
                formatter: (_, value) => fmtSvc.formatInt(value as number),
                id: 'statements',
                type: 'number',
                align: 'right',
                filter: true,
            },
            {
                header: 'Statement Tag',
                accessor: 'tags',
                id: 'Tags',
                defaultHidden: true,
                sortField: 'tags',
                type: 'string',
                filter: {
                    name: 'Statement Tag',
                    filterField: 'tags',
                    filterType: 'string',
                    options: {
                        getValueProvider: () => async () => tagKeys.keys,
                    },
                    valueRenderer: (props) => <TagKeyDataFilter {...props} tagKeys={tagKeys.keys} />,
                },
            },
            {
                header: 'Connected',
                accessor: 'connected',
                id: 'Connected',
                defaultHidden: true,
                sortField: 'connected',
                type: 'boolean',
                filter: {
                    name: 'Connected',
                    filterField: 'connected',
                    filterType: 'boolean',
                },
            },
            {
                header: 'Compliant',
                accessor: 'compliant',
                sortField: 'compliant',
                defaultWidth: 150,
                formatter: (_, value) => fmtSvc.formatInt(value as number),
                id: 'compliant',
                type: 'number',
                align: 'right',
                filter: true,
            },
            {
                header: 'Non-Compliant',
                accessor: 'nonCompliant',
                sortField: 'nonCompliant',
                defaultWidth: 150,
                formatter: (_, value) => fmtSvc.formatInt(value as number),
                id: 'nonCompliant',
                type: 'number',
                align: 'right',
                filter: true,
            },
            {
                header: 'Total',
                accessor: 'total',
                sortField: 'total',
                defaultWidth: 150,
                formatter: (_, value) => fmtSvc.formatInt(value as number),
                id: 'Total',
                align: 'right',
                type: 'number',
                filter: true,
            },
            {
                header: 'Compliance',
                accessor: (item) => (item.compliant + item.nonCompliant > 0 ? (item.compliant / (item.compliant + item.nonCompliant)) * 100 : 0),
                cellRenderer: (item) => {
                    return (
                        <Group>
                            <div style={{ marginTop: '2px', width: '100px' }}>
                                <Progress
                                    radius="md"
                                    size="md"
                                    value={item.compliant + item.nonCompliant > 0 ? (item.compliant / (item.compliant + item.nonCompliant)) * 100 : 0}
                                />
                            </div>
                            <div style={{ textAlign: 'right', width: '40px' }}>
                                {Math.round(
                                    item.compliant + item.nonCompliant > 0 ? (item.compliant / (item.compliant + item.nonCompliant)) * 100 : 0
                                )}
                                %
                            </div>
                            <div style={{ textAlign: 'right', width: '100px' }}>
                                <i className="ti ti-chevron-right"></i>
                            </div>
                        </Group>
                    );
                },
                defaultWidth: 300,
                id: 'Compliance',
                align: 'left',
                type: 'number',
            },
        ] as ColumnConfig<EffectiveTagPoliciesDataItem>[];
    }, [effPolicies, effPolicyDataItems]);

    const defaultState = useMemo(() => {
        const { builder: xb } = exprBuilder<EffectiveTagPoliciesDataItem>();
        const filters: IQueryExpr[] = [xb.resolve(tag ? xb.model.tags.eq([tag]) : xb.model.connected.eq(true))];
        return {
            columns: cols.filter((c) => !c.defaultHidden).map((c) => ({ id: c.id, width: c.defaultWidth })),
            filters,
            sort: [],
        } as DataGridState;
    }, [tag, cols]);

    var chartProps = useMemo(
        () => ({
            settings: {
                noWrapper: true,
            } as PieChartSettings,
        }),
        []
    );

    return (
        <Stack sx={{ height: '100%' }} spacing={10}>
            <PanelHeader>
                <Grid sx={{ flex: 1 }}>
                    <Grid.Col span={6}>
                        <Container>
                            <Header>Resource Policy Coverage</Header>
                            <div style={{ textAlign: 'center' }}>
                                <PercentCovered>
                                    {totalResources === 0 ? (
                                        <>&mdash;</>
                                    ) : (
                                        fmtSvc.formatPercent((numInCompliance + numNotInCompliance) / totalResources)
                                    )}
                                </PercentCovered>
                                <Group position="apart" align="center" spacing={0}>
                                    <CoverageText style={{ fontWeight: 'bold', background: colors.gray[2] as CustomColors }}>
                                        <br />
                                        Covered by Policy
                                    </CoverageText>
                                    <CoverageText style={{ fontWeight: 'bold', background: colors.gray[2] as CustomColors }}>
                                        Not Covered
                                        <br />
                                        by Policy
                                    </CoverageText>
                                    <CoverageText style={{ fontWeight: 'bold', background: colors.gray[2] as CustomColors }}>
                                        <br />
                                        Total
                                    </CoverageText>
                                </Group>
                                <Group position="apart" align="center" spacing={0}>
                                    <CoverageText>{fmtSvc.formatInt(numInCompliance + numNotInCompliance)}</CoverageText>
                                    <CoverageText>{fmtSvc.formatInt(totalResources - (numInCompliance + numNotInCompliance))}</CoverageText>
                                    <CoverageText>{fmtSvc.formatInt(totalResources)}</CoverageText>
                                </Group>
                            </div>
                        </Container>
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Container>
                            <Header>Tag Keys Covered by Policies</Header>
                            {numInCompliance + numNotInCompliance > 0 ? (
                                <div style={{ height: '150px' }}>
                                    <PieChart
                                        {...chartProps}
                                        groups={['x']}
                                        values={['y']}
                                        data={[
                                            { x: 'Covered', y: policyTagKeys },
                                            { x: 'Not Covered', y: totalTagKeys - policyTagKeys },
                                        ]}
                                    />
                                </div>
                            ) : (
                                <NoEffectivePolicies>No effective policies</NoEffectivePolicies>
                            )}
                        </Container>
                    </Grid.Col>
                </Grid>
            </PanelHeader>
            <PanelBody style={{ paddingTop: 0 }}>
                <Container style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Header style={{ paddingBottom: '16px' }}>Effective Tag Policies by Account</Header>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <FillerSwitch loading={!effPolicyDataItems}>
                            {() => (
                                <>
                                    {!effPolicyDataItems ? null : (
                                        <DataGrid
                                            dataSource={effPolicyDataItems}
                                            columns={cols}
                                            hideHeader
                                            state={defaultState}
                                            selection={selectedEffectiveTagPolicy}
                                            selectionMode="single"
                                            rowStyle={(item, _, hovered) => ({
                                                background: hovered && item.connected ? colors.primary[2] : 'none',
                                                cursor: item.connected ? 'pointer' : 'default',
                                            })}
                                            onRowClick={loadEffectiveTagPolicies}
                                        />
                                    )}
                                </>
                            )}
                        </FillerSwitch>
                    </div>
                </Container>
            </PanelBody>
        </Stack>
    );
};
